import "./PersonalInfo.css";
import { memo, useState, useEffect } from "react";
import moment from "moment";
import ProfileSubHeaderLinks from "../../components/profileSubHeaderLinks/ProfileSubHeaderLinks.jsx";
import { Joyride } from "react-joyride";
import { getUserInformations, updateUserRequest } from "../../services/wisdomServices/userService";
import { formatDate } from "../../utils/helper/date";
import { getSessionItem, setSessionItem } from "../../utils/helper/session";
import { timezones } from "../../utils/helper/timezones";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { requestGetMyRate } from "../../services/wisdomServices/rateService";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { parseBirthday } from "../../utils/helper/parseUserBirthday.js";
import { certificateTemplate } from "../../utils/helper/certificateTemplate.js";
import { useOnboarding } from "../../context/onboardingContext.jsx";
import { RequestGetApplicantById } from "../../services/wisdomServices/applicantsService.js";
import { requestUpdateApplicant } from "../../services/wisdomServices/applicantService.js";
import { isValidEmail, isValidPhoneNumber } from "../../utils/helper/validations.js";
import { toast } from "react-toastify";
import { MultiSelect } from "primereact/multiselect";
import { requestGetTalentsCatalog } from "../../services/wisdomServices/talentService.js";

const MemoizedProfileSubHeaderLinks = memo(ProfileSubHeaderLinks);

/**
 * @function PersonalInfo
 * @returns {JSX.Element} - personal information screen
 */
function PersonalInfo() {
  const user = getSessionItem("User")
  const [headerUser, setHeaderUser] = useState(() => getSessionItem("User"));
  const isApplicant = user?.roleName == "Applicant" || user?.role == 109;
  const { runTour, goToNextPage } = useOnboarding();
  const [originalUserDetails, setOriginalUserDetails] = useState(null);
  const [skills, setSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const requiredFields = [
    "firstName",
    "lastName",
    "phone",
    "pronoun",
    "birthday",
    "city",
    "emergencyContact",
    "personalEmail",
    "address",
    "zipcode"
  ];
  const [userDetails,setUserDetails] = useState({});

  useEffect(() => {
    if (!user?.id) return;

    if (isApplicant) {
      loadApplicantData(user.id);
    } else {
      handleGetUserInformations(user.id);
    }
  }, []);

  const getMissingApplicantFields = (applicant) => {
    const fieldsToValidate = [
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
      "city",
      "address",
      "zipcode",
      "birthday",
      "pronoun",
      "timezone",
      "skills",
      "languages"
    ];

    return fieldsToValidate.filter((field) => {
      const value = applicant?.[field];

      if (Array.isArray(value)) {
        return value.length === 0;
      }

      return value === null || value === undefined || String(value).trim() === "";
    });
  };

  const normalizeCatalogResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const normalizeSelectedIds = (value) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item) => {
        if (typeof item === "object" && item !== null) return Number(item.id);
        return Number(item);
      })
      .filter((id) => Number.isFinite(id));
  };

  const mapCatalogValuesToIds = (value, catalog) => {
    if (!value || !catalog?.length) return [];

    if (Array.isArray(value)) {
      return normalizeSelectedIds(value);
    }

    if (typeof value !== "string") {
      return [];
    }

    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((catalogValue) => {
        const found = catalog.find(
          (catalogItem) =>
            String(catalogItem.value).toLowerCase() === catalogValue.toLowerCase()
        );

        return found?.id;
      })
      .filter(Boolean);
  };

  const buildApplicantFormData = (applicant, skillsCatalog, languagesCatalog) => {
    const selectedSkills = mapCatalogValuesToIds(applicant?.skills, skillsCatalog);
    const selectedLanguages = mapCatalogValuesToIds(applicant?.languages, languagesCatalog);

    return {
      ...applicant,
      skills: selectedSkills,
      languages: selectedLanguages,
      catalog: {
        SKILL: selectedSkills,
        LANGUAGE: selectedLanguages
      },
      role: 109,
      roleName: "Applicant",
      birthday: applicant?.birthday ? parseBirthday(applicant.birthday) : null
    };
  };

  const isEqual = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const loadSkills = async() => {
    try {
      setLoadingSkills(true);
      const response = await requestGetTalentsCatalog("SKILL");
      const normalizedSkills = normalizeCatalogResponse(response);

      setSkills(normalizedSkills);

      return normalizedSkills;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setLoadingSkills(false);
    }
  }

  const loadLanguages = async() => {
    try {
      setLoadingLanguages(true);
      const response = await requestGetTalentsCatalog("LANGUAGE");
      const normalizedLanguages = normalizeCatalogResponse(response);

      setLanguages(normalizedLanguages);

      return normalizedLanguages;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setLoadingLanguages(false);
    }
  }

  const isMissingValue = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return value === null || value === undefined || String(value).trim() === "";
  };

  const isFieldMissing = (field) => {
    return missingFields.includes(field);
  };

  const getFieldWrapperClass = (field) => {
    return `p-inputgroup flex-column flex-1 ${isFieldMissing(field) ? "required-field-error" : ""}`;
  };

  const getInputClass = (field, extraClass = "") => {
    return `${extraClass} ${isFieldMissing(field) ? "p-invalid" : ""}`.trim();
  };

  const updateUserDetail = (field, value) => {
    setUserDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (!isMissingValue(value)) {
      setMissingFields((prev) => prev.filter((item) => item !== field));
    }
  };


  const loadApplicantData = async (userId) => {
    try {
      const [applicantResponse, skillsCatalog, languagesCatalog] = await Promise.all([
        RequestGetApplicantById(userId),
        loadSkills(),
        loadLanguages()
      ]);

      const formattedApplicant = buildApplicantFormData(
        applicantResponse,
        skillsCatalog,
        languagesCatalog
      );

      const missingApplicantFields = getMissingApplicantFields(formattedApplicant);

      setMissingFields(missingApplicantFields);

      if (missingApplicantFields.length > 0) {
        toast.info(
          "We noticed that your profile has some missing information. Completing it can help improve your chances with future opportunities.",
          {
            position: "bottom-center",
            theme: "colored",
          }
        );
      }

      setUserDetails(formattedApplicant);
      setOriginalUserDetails(formattedApplicant);
    } catch (error) {
      console.error(`Error: ${error}`);
    }
  };

  const isFormValid = () => {
    return requiredFields.every(field => {
      const value = userDetails[field];
      return value && String(value).trim() !== "";
    });
  };

  /**
   * @async
   * @function saveChanges
   * @description - saves updated personal information to the backend and updates session data
   */
  const saveChanges = async (e) => { 
    e.preventDefault()

    if (!isValidPhoneNumber(userDetails.phone)) {
      toast.error("Invalid phone number format. Please use + followed by 8 to 15 digits.");
      return;
    }

    if(!isValidPhoneNumber(userDetails.emergencyContact)) {
      toast.error("Invalid emergency contact phone number format. Please use + followed by 8 to 15 digits.");
      return;
    }

    if(!isValidEmail(userDetails.personalEmail)){ 
      toast.error("Invalid personal email format. Please enter a valid email address.");
      return;
    }

    const formattedBirthday = userDetails.birthday
      ? moment(userDetails.birthday).format("YYYY-MM-DD")
      : null;

    let keys = {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      phone: userDetails.phone,
      pronoun: userDetails.pronoun,
      timezone: userDetails.timezone,
      birthday: formattedBirthday,
      city: userDetails.city,
      emergencyContact: userDetails.emergencyContact,
      personalEmail: userDetails.personalEmail,
      address: userDetails.address,
      zipcode: userDetails.zipcode
    }
    try {
      let rsummary = await updateUserRequest(userDetails.id, keys);
      const formattedUser = {
        ...rsummary,
        dateJoined: formatDate(rsummary.dateJoined),
        birthday: parseBirthday(rsummary.birthday),
        contractEndDate: formatDate(rsummary.contractEndDate),
        offboardDate: formatDate(rsummary.offboardDate),
      };
      setUserDetails(formattedUser);
      setSessionItem("User", rsummary);
      setHeaderUser(rsummary);

    } catch (error) {
      console.error(error)
    }

  }

  /**
   * @async
   * @function saveApplicantChanges
   * @description - saves updated personal information to the backend and updates session data
   */
  const saveApplicantChanges = async (e) => {
    e.preventDefault()

    const missingApplicantFields = getMissingApplicantFields(userDetails);

    if (missingApplicantFields.length > 0) {
      setMissingFields(missingApplicantFields);

      toast.error("Please fill in all required fields before saving.", {
        position: "bottom-center",
        theme: "colored",
      });

      return;
    }

    if (!isValidPhoneNumber(userDetails.phoneNumber)) {
      toast.error("Invalid phone number format. Please use + followed by 8 to 15 digits.");
      return;
    }

    if(!isValidEmail(userDetails.email)){ 
      toast.error("Invalid email format. Please enter a valid email address.");
      return;
    }

    const formattedBirthday = userDetails.birthday
      ? moment(userDetails.birthday).format("YYYY-MM-DD")
      : null;

    const selectedSkills = normalizeSelectedIds(userDetails.skills);
    const selectedLanguages = normalizeSelectedIds(userDetails.languages);

    const fieldsMap = {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      email: userDetails.email,
      phoneNumber: userDetails.phoneNumber,
      address: userDetails.address,
      city: userDetails.city,
      birthday: formattedBirthday,
      pronoun: userDetails.pronoun,
      zipcode: userDetails.zipcode,
      timezone: userDetails.timezone,
      catalog: {
        SKILL: selectedSkills,
        LANGUAGE: selectedLanguages
      }
    };

    const changedFields = {};

    Object.keys(fieldsMap).forEach((key) => {
      const originalValue = originalUserDetails?.[key];
      const currentValue = fieldsMap[key];

      if (!isEqual(currentValue, originalValue)) {
        changedFields[key] = currentValue;
      }
    });

    if (Object.keys(changedFields).length === 0) {
      console.warn("Nothing changed, no update needed.");
      return;
    }

    try {
      let updatedUser = await requestUpdateApplicant(userDetails.id, changedFields);
      const formattedUser = buildApplicantFormData(updatedUser, skills, languages);

      setUserDetails(formattedUser);
      setOriginalUserDetails(formattedUser);
      setSessionItem("User", formattedUser);
      setHeaderUser(formattedUser);

    } catch (error) {
      console.error(error);
    }
  };

  /**
   * @async
   * @function generatePDF
   * @description - generates and downloads a PDF employment confirmation letter
   */
  const generatePDF = async() => {
    try {
      const userInfo = await getUserInformations(userDetails.id)
      const userRate = await requestGetMyRate()
      const pdf = await certificateTemplate(
        `${userInfo.firstName}  ${userInfo.lastName} `,
        `${userInfo.address} - ${userInfo.city}`,
        `$${userRate.rate}`,
        `${userInfo.jobDescription}`,
        `${formatDate(userInfo.startWorkDate)}`,
        `${formatDate(new Date())}`,
      )
      pdf.download("certificate-of-service.pdf")
    } catch (error) {
      console.error(error)
    }
  };

  async function handleGetUserInformations(userId){
    try {
      const response = await getUserInformations(userId);
      const user = response;
      const formattedUser = {
        ...user,
        dateJoined: formatDate(user.dateJoined),
        birthday: parseBirthday(user.birthday),
        contractEndDate: formatDate(user.contractEndDate),
        offboardDate: formatDate(user.offboardDate),
      };

      setUserDetails(formattedUser);
    } catch (error) {
      console.error(`Error: ${error}`)
    }
  }

  const contractUrl = () => {
    window.open(userDetails.contractUrl, '_blank')
  }

  const handleJoyrideCallback = (data) => {
    const { action, index, type } = data;

    if (
      type === "step:after" &&
      action === "next" &&
      index === steps.length - 1
    ) {
      goToNextPage();
    }
  };

  const steps= [
    {
      content: "Here you can find your personal information on the system.",
      title: "Personal Info",
      placement: "center",
      target: 'body',
      disableBeacon: true,

    },
    {
      content: 'The detail section houses all your basic information. Please edit/add your details to complete your profile before continuing.',
      placement: "bottom",
      target: "#step-4",
      title: "Details",
      spotlightClicks: true
    }
  ]

  const handlePhoneChange = (e, key) => {
    let value = e.target.value;

    value = value.replace(/[^\d+]/g, "");

    value = value.replace(/\+/g, "");
    value = "+" + value;

    setUserDetails({
      ...userDetails,
      [key]: value,
    });
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        callback={handleJoyrideCallback}
        locale={{
          next: "Next",
          last: "Next Page",
          skip: "Skip tour",
          back: "Back"
        }}
        styles={{
          options: {
            primaryColor: "#0BADDE",
            zIndex: 10000
          },
          tooltip: {
            borderRadius: "8px"
          },
          buttonNext: {
            backgroundColor: "#0BADDE"
          }
        }}
      />
      <div className="profileMain">
        <MemoizedProfileSubHeaderLinks user={headerUser} />
        <div className="employee-main my-profile-info" id="step-4">
          <div className="employee-main-content">
            <div className="user-information-page-title">
              <h1 className="personal-info-header">Details</h1>
            </div>
            {userDetails.contractUrl && (
              <div className="user-profile-contracts-buttons">
                <Button label="Certificate of Service" icon="pi pi-file-pdf" onClick={generatePDF} severity="success"/>
                <Button link label="Open Contract" icon="pi pi-external-link" onClick={contractUrl}/>
              </div>
            )}
            {isApplicant ? (
              <form onSubmit={saveApplicantChanges}>
                {/* GROUP 1 */}
                <div className="user-informations-form-group">
                  <div className={getFieldWrapperClass("firstName")}>
                    <label htmlFor="applicant-first-name">First name *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-user"></i>
                      </span>

                      <InputText
                        id="applicant-first-name"
                        required
                        className={getInputClass("firstName")}
                        value={userDetails.firstName ?? ""}
                        onChange={(e) => updateUserDetail("firstName", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("lastName")}>
                    <label htmlFor="applicant-last-name">Last name *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-user"></i>
                      </span>

                      <InputText
                        id="applicant-last-name"
                        required
                        className={getInputClass("lastName")}
                        value={userDetails.lastName ?? ""}
                        onChange={(e) => updateUserDetail("lastName", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("pronoun")}>
                    <label htmlFor="user-profile-infos-pronoun">Preferred pronoun *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-bars"></i>
                      </span>

                      <InputText
                        id="user-profile-infos-pronoun"
                        required
                        className={getInputClass("pronoun")}
                        value={userDetails.pronoun ?? ""}
                        onChange={(e) => updateUserDetail("pronoun", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* GROUP 2 */}
                <div className="user-informations-form-group">
                  <div className={getFieldWrapperClass("zipcode")}>
                    <label htmlFor="user-profile-infos-zip-code">ZIP code *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-info-circle"></i>
                      </span>

                      <InputText
                        id="user-profile-infos-zip-code"
                        required
                        className={getInputClass("zipcode")}
                        value={userDetails.zipcode ?? ""}
                        onChange={(e) => updateUserDetail("zipcode", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("address")}>
                    <label htmlFor="applicant-address">Address *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-map"></i>
                      </span>

                      <InputText
                        id="applicant-address"
                        required
                        placeholder="Enter your address"
                        className={getInputClass("address")}
                        value={userDetails.address ?? ""}
                        onChange={(e) => updateUserDetail("address", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("city")}>
                    <label htmlFor="applicant-city">City/Country *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-map-marker"></i>
                      </span>

                      <InputText
                        id="applicant-city"
                        required
                        className={getInputClass("city")}
                        value={userDetails.city ?? ""}
                        onChange={(e) => updateUserDetail("city", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* GROUP 3 */}
                <div className="user-informations-form-group">
                  <div className={getFieldWrapperClass("email")}>
                    <label htmlFor="applicant-email">Email *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-envelope"></i>
                      </span>

                      <InputText
                        id="applicant-email"
                        required
                        className={getInputClass("email")}
                        value={userDetails.email ?? ""}
                        onChange={(e) => updateUserDetail("email", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("phoneNumber")}>
                    <label htmlFor="applicant-phone-number">Phone *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-phone"></i>
                      </span>

                      <InputText
                        type="tel"
                        id="applicant-phone-number"
                        required
                        className={getInputClass("phoneNumber")}
                        value={userDetails.phoneNumber ?? ""}
                        onChange={(e) => {
                          handlePhoneChange(e, "phoneNumber");

                          if (!isMissingValue(e.target.value)) {
                            setMissingFields((prev) =>
                              prev.filter((item) => item !== "phoneNumber")
                            );
                          }
                        }}
                        minLength={9}
                        maxLength={16}
                        placeholder="+55 (47) 99999-9999"
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("birthday")}>
                    <label htmlFor="user-profile-infos-birthday">Birthday *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-calendar"></i>
                      </span>

                      <Calendar
                        id="user-profile-infos-birthday"
                        value={userDetails.birthday ? new Date(userDetails.birthday) : null}
                        onChange={(e) => updateUserDetail("birthday", e.value)}
                        dateFormat="dd/mm/yy"
                        maxDate={new Date()}
                        className={getInputClass("birthday", "w-full")}
                      />
                    </div>
                  </div>
                </div>

                {/* GROUP 4 */}
                <div className="user-informations-form-group">
                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="applicant-joined-date">Joined Date</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-calendar"></i>
                      </span>

                      <InputText
                        id="applicant-joined-date"
                        disabled
                        readOnly
                        value={formatDate(userDetails.createdAt)}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("skills")}>
                    <label htmlFor="applicant-skills">Skills *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-list-check"></i>
                      </span>

                      <MultiSelect
                        inputId="applicant-skills"
                        maxSelectedLabels={2}
                        value={userDetails.skills ?? []}
                        options={skills}
                        loading={loadingSkills}
                        filter
                        optionLabel="value"
                        optionValue="id"
                        placeholder="Select your skills"
                        className={getInputClass("skills", "w-full")}
                        onChange={(e) => {
                          const value = e.value ?? [];

                          setUserDetails((prev) => ({
                            ...prev,
                            skills: value,
                            catalog: {
                              ...(prev.catalog ?? {}),
                              SKILL: value,
                            },
                          }));

                          if (value.length > 0) {
                            setMissingFields((prev) =>
                              prev.filter((item) => item !== "skills")
                            );
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className={getFieldWrapperClass("languages")}>
                    <label htmlFor="applicant-languages">Languages *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-list-check"></i>
                      </span>

                      <MultiSelect
                        inputId="applicant-languages"
                        maxSelectedLabels={2}
                        value={userDetails.languages ?? []}
                        options={languages}
                        loading={loadingLanguages}
                        filter
                        optionLabel="value"
                        optionValue="id"
                        placeholder="Select your languages"
                        className={getInputClass("languages", "w-full")}
                        onChange={(e) => {
                          const value = e.value ?? [];

                          setUserDetails((prev) => ({
                            ...prev,
                            languages: value,
                            catalog: {
                              ...(prev.catalog ?? {}),
                              LANGUAGE: value,
                            },
                          }));

                          if (value.length > 0) {
                            setMissingFields((prev) =>
                              prev.filter((item) => item !== "languages")
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* GROUP 5 */}
                <div className="user-informations-form-group">
                  <div className={getFieldWrapperClass("timezone")}>
                    <label htmlFor="applicant-timezone-select">Select your Timezone *</label>

                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-clock"></i>
                      </span>

                      <Dropdown
                        id="applicant-timezone-select"
                        placeholder="Select your timezone"
                        options={timezones}
                        value={userDetails.timezone}
                        onChange={(e) => updateUserDetail("timezone", e.value)}
                        filter
                        filterDelay={400}
                        filterPlaceholder="Search by name"
                        className={getInputClass("timezone", "w-full")}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1 invisible-placeholder" />
                  <div className="p-inputgroup flex-column flex-1 invisible-placeholder" />
                </div>

                <div className="user-info-form-action-box">
                  <Button
                    label="Save changes"
                    type="submit"
                    severity="success"
                  />
                </div>
              </form>
            ) : (
              <form onSubmit={saveChanges}>

                {/* Group 1 */}
                <div className="user-informations-form-group">
                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-first-name">First name</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-user"></i></span>
                      <InputText
                        id="user-profile-infos-first-name"
                        required
                        value={userDetails.firstName ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, firstName: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-last-name">Last name</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-user"></i></span>
                      <InputText
                        id="user-profile-infos-last-name"
                        required
                        value={userDetails.lastName ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, lastName: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-pronoun">Preferred pronoun</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-bars"></i></span>
                      <InputText
                        id="user-profile-infos-pronoun"
                        required
                        value={userDetails.pronoun ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, pronoun: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-job-title">Job title</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-briefcase"></i></span>
                      <InputText
                        id="user-profile-infos-job-title"
                        readOnly
                        disabled
                        value={userDetails.jobDescription ?? ''}
                      />
                    </div>
                  </div>

                </div>

                {/* Group 2 */}
                <div className="user-informations-form-group">
                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-phone">Phone</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-phone"></i></span>
                      <InputText 
                        type="tel"
                        id="user-profile-infos-phone" 
                        required 
                        value={userDetails.phone ?? ''} 
                        onChange={(e) => handlePhoneChange(e, 'phone')}
                        minLength={9}
                        maxLength={16}
                        placeholder="Phone Number"
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-personal-email">Personal email</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-envelope"></i></span>
                      <InputText
                        id="user-profile-infos-personal-email"
                        required
                        value={userDetails.personalEmail ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, personalEmail: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-emergency-contact">Emergency contact</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-phone"></i></span>
                      <InputText
                        id="user-profile-infos-emergency-contact"
                        type="tel"
                        minLength={9}
                        maxLength={16}
                        required
                        value={userDetails.emergencyContact ?? ''}
                        onChange={(e) => handlePhoneChange(e, 'emergencyContact')}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-birthday">Birthday</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-calendar"></i></span>
                      <Calendar
                        id="user-profile-infos-birthday"
                        value={userDetails.birthday}
                        onChange={(e) => setUserDetails(prev => ({ ...prev, birthday: e.value }))}
                        dateFormat="dd/mm/yy"
                        placeholder="dd/mm/yyyy"
                        className="w-full"
                        maxDate={new Date()}
                      />
                    </div>
                  </div>

                </div>

                {/* Group 3 */}
                <div className="user-informations-form-group">
                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-city">City/Country</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-map"></i></span>
                      <InputText
                        id="user-profile-infos-city"
                        required
                        value={userDetails.city ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, city: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-address">Address</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-map-marker"></i></span>
                      <InputText
                        id="user-profile-infos-address"
                        required
                        value={userDetails.address ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, address: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-zip-code">ZIP code</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-info-circle"></i></span>
                      <InputText
                        id="user-profile-infos-zip-code"
                        required
                        value={userDetails.zipcode ?? ''}
                        onChange={(e) => setUserDetails({...userDetails, zipcode: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1 user-profile-infos-timezone">
                    <label htmlFor="timezone-select">Select your Timezone</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">
                        <i className="pi pi-clock"></i>
                      </span>

                      <Dropdown
                        id="timezone-select"
                        placeholder="Select your timezone"
                        options={timezones}
                        value={userDetails.timezone}
                        onChange={(e) => setUserDetails(prev => ({ ...prev, timezone: e.value }))}
                        filter
                        filterDelay={400}
                        filterPlaceholder="Search by name"
                        className="w-full"
                      />
                    </div>
                  </div>

                </div>

                {/* Group 4 */}
                <div className="user-informations-form-group">
                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-username">Email</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-envelope"></i></span>
                      <InputText
                        id="user-profile-infos-username"
                        readOnly
                        disabled
                        value={userDetails.username ?? ''}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-date-joined">Date joined</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-calendar-clock"></i></span>
                      <InputText
                        id="user-profile-infos-date-joined"
                        disabled
                        readOnly
                        value={formatDate(userDetails.startWorkDate) ?? ''}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-contract-expire-date">Contract expire date</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-calendar"></i></span>
                      <InputText
                        id="user-profile-infos-contract-expire-date"
                        disabled
                        readOnly
                        value={userDetails.contractEndDate ?? ''}
                      />
                    </div>
                  </div>

                  <div className="p-inputgroup flex-column flex-1">
                    <label htmlFor="user-profile-infos-contract-status">Contract status</label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon"><i className="pi pi-exclamation-circle"></i></span>
                      <InputText
                        id="user-profile-infos-contract-status"
                        disabled
                        readOnly
                        value={userDetails.contractStatus ?? ''}
                      />
                    </div>
                  </div>

                </div>

                <div className="user-info-form-action-box">
                  <Button 
                      label="Save changes"
                      severity="success"
                      type="submit"
                      disabled={!isFormValid()}
                    />
                    <Message text="All fields are mandatory" severity="warn"/>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default PersonalInfo;